import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "./ui/navigation-menu";

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const capabilities = [
    { name: 'CNC Machining', path: '/capabilities/cnc-machining' },
    { name: 'Plastic Injection Molding', path: '/capabilities/injection-molding' },
    { name: 'Die Casting', path: '/capabilities/die-casting' },
    { name: 'Sheet Metal Fabrication', path: '/capabilities/sheet-metal' },
    { name: 'Aluminum Extrusion', path: '/capabilities/aluminum-extrusion' },
    { name: 'Assembly & Box Build', path: '/capabilities/assembly' }
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-smooth ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-white'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center transition-smooth hover:opacity-80">
            <img 
              src="/logo.png" 
              alt="All Parts Manufacturing - Precision in every part" 
              className="h-10 sm:h-12 md:h-14 w-auto max-w-[180px] sm:max-w-[220px] md:max-w-[260px]"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="text-foreground hover:text-primary transition-smooth">
                    Capabilities
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-3 p-4">
                      {capabilities.map((item) => (
                        <li key={item.path}>
                          <NavigationMenuLink asChild>
                            <Link
                              to={item.path}
                              className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-smooth hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                            >
                              <div className="text-sm font-medium leading-none">{item.name}</div>
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            <Link to="/quality-process" className="text-sm font-medium text-foreground hover:text-primary transition-smooth">
              Quality & Process
            </Link>
            <Link to="/global-network" className="text-sm font-medium text-foreground hover:text-primary transition-smooth">
              Global Network
            </Link>
            <Link to="/about" className="text-sm font-medium text-foreground hover:text-primary transition-smooth">
              About
            </Link>
            <Link to="/careers" className="text-sm font-medium text-foreground hover:text-primary transition-smooth">
              Careers
            </Link>
            <Link to="/blog" className="text-sm font-medium text-foreground hover:text-primary transition-smooth">
              Blog
            </Link>
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <Link to="/contact">
              <Button variant="outline" size="sm" className="transition-smooth">
                Book a Call
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="sm" className="bg-accent hover:bg-accent/90 transition-smooth">
                Request a Quote
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-md text-foreground hover:bg-secondary transition-smooth"
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-border">
          <div className="px-4 py-6 space-y-4">
            <div className="space-y-2">
              <div className="font-medium text-sm text-muted-foreground px-3">Capabilities</div>
              {capabilities.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="block px-3 py-2 text-sm font-medium text-foreground hover:bg-secondary rounded-md transition-smooth"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>
            <Link
              to="/quality-process"
              className="block px-3 py-2 text-sm font-medium text-foreground hover:bg-secondary rounded-md transition-smooth"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Quality & Process
            </Link>
            <Link
              to="/global-network"
              className="block px-3 py-2 text-sm font-medium text-foreground hover:bg-secondary rounded-md transition-smooth"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Global Network
            </Link>
            <Link
              to="/about"
              className="block px-3 py-2 text-sm font-medium text-foreground hover:bg-secondary rounded-md transition-smooth"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              About
            </Link>
            <Link
              to="/careers"
              className="block px-3 py-2 text-sm font-medium text-foreground hover:bg-secondary rounded-md transition-smooth"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Careers
            </Link>
            <Link
              to="/blog"
              className="block px-3 py-2 text-sm font-medium text-foreground hover:bg-secondary rounded-md transition-smooth"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Blog
            </Link>
            <div className="pt-4 space-y-2">
              <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
                <Button variant="outline" className="w-full transition-smooth">
                  Book a Call
                </Button>
              </Link>
              <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
                <Button className="w-full bg-accent hover:bg-accent/90 transition-smooth">
                  Request a Quote
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
